import React, { Suspense } from "react";

import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { t } from "@impero-com/common/utils/i18n";
import { FormField, FormLabel } from "@impero-com/ui";
import { Typography } from "antd";
import { cx } from "class-variance-authority";
import { RefCallBack } from "react-hook-form";

import { NewUserTag } from "api/generated/model";
import { EditableBaseTagListPlus } from "components/tag/editableBaseTagListPlus/editableTagListPlus";
import { UserTag } from "components/tag/editableBaseTagListPlus/types";
import {
    createNewUserTag,
    getUserTagCategoryId,
    isUserTagActive,
} from "components/tag/editableBaseTagListPlus/utils";
import { useCreateTagMutation } from "org/tags/queries/useCreateTagMutation";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { Guid } from "utils/guid";

import { EditableTagListInputSkeleton } from "../skeleton/editableTagListInputSkeleton";

const { Text } = Typography;

export interface EditableUserTagListInputBaseProps {
    disabled?: boolean;
    label?: React.ReactNode;
    canCreateTag?: boolean;
    cascaderClassName?: string;
}

interface EditableUserTagListPlusInputProps extends EditableUserTagListInputBaseProps {
    error?: string | undefined;
    selectedTagIds: Guid[];
    readOnlyTagIds?: Guid[];
    setSelectedTagIds: (tagIds: Guid[]) => void;
    inputRef?: RefCallBack;
    onBlur?: () => void;
}
export function EditableUserTagListPlusInput({
    setSelectedTagIds,
    cascaderClassName,
    error,
    onBlur,
    inputRef,
    label,
    selectedTagIds,
    readOnlyTagIds,
    canCreateTag = false,
    disabled = false,
}: EditableUserTagListPlusInputProps) {
    return (
        <FormField
            fullWidth
            error={error}
            className={cx(error && ERRORED_CLASSNAME)}
        >
            {label && <FormLabel disabled={disabled}>{label}</FormLabel>}
            <EditableUserTagListPlusContent
                setSelectedTagIds={setSelectedTagIds}
                selectedTagIds={selectedTagIds}
                readOnlyTagIds={readOnlyTagIds}
                cascaderClassName={cascaderClassName}
                onBlur={onBlur}
                inputRef={inputRef}
                canCreateTag={canCreateTag}
                disabled={disabled}
            />
        </FormField>
    );
}

function EditableUserTagListPlusContent({
    setSelectedTagIds,
    selectedTagIds,
    readOnlyTagIds,
    cascaderClassName,
    onBlur,
    inputRef,
    canCreateTag = false,
    disabled = false,
}: EditableUserTagListPlusInputProps) {
    const showNoTagsLabel = disabled && selectedTagIds.length === 0;

    if (showNoTagsLabel) {
        return <Text type="secondary">{t("Control_Tags_NotAssigned")}</Text>;
    }
    return (
        <Suspense
            fallback={
                <EditableTagListInputSkeleton count={selectedTagIds.length} />
            }
        >
            <EditableUserTagList
                selectedTagIds={selectedTagIds}
                readOnlyTagIds={readOnlyTagIds}
                cascaderClassName={cascaderClassName}
                canCreateTag={canCreateTag}
                disabled={disabled}
                inputRef={inputRef}
                onBlur={onBlur}
                setSelectedTagIds={setSelectedTagIds}
            />
        </Suspense>
    );
}

interface EditableUserTagListProps {
    disabled: boolean;
    canCreateTag: boolean;
    setSelectedTagIds: (tagIds: Guid[]) => void;
    selectedTagIds: Guid[];
    readOnlyTagIds?: Guid[];
    cascaderClassName?: string;
    inputRef?: RefCallBack;
    onBlur?: () => void;
}
function EditableUserTagList({
    disabled,
    canCreateTag,
    setSelectedTagIds,
    selectedTagIds,
    readOnlyTagIds,
    cascaderClassName,
    onBlur,
    inputRef,
}: EditableUserTagListProps) {
    const { data } = useTagsSuspenseQuery();
    const { mutateAsync: createTag } = useCreateTagMutation();
    const handleCreateTag = async (tag: NewUserTag) => {
        if (!canCreateTag) {
            throw new Error(
                "It should not be possible to call 'handleCreateTag' when 'canCreateTag' is 'false'"
            );
        }

        const response = await createTag({ tag });
        const newId = response.id;
        setSelectedTagIds([...selectedTagIds, newId]);
    };
    return (
        <EditableBaseTagListPlus<UserTag>
            onBlur={onBlur}
            inputRef={inputRef}
            allTagCategories={data.tagCategories}
            allTags={data.tags}
            isTagActive={isUserTagActive}
            getTagCategoryId={getUserTagCategoryId}
            disabled={disabled}
            canCreateTag={canCreateTag}
            selectedTagIds={selectedTagIds}
            readOnlyTagIds={readOnlyTagIds}
            cascaderClassName={cascaderClassName}
            onCreateTag={(name, categoryId) =>
                void handleCreateTag(createNewUserTag(name, categoryId))
            }
            setSelectedTagIds={(tags) => void setSelectedTagIds(tags)}
        />
    );
}
